import DataQuery from 'data/DataQuery';
import { Customer_ContractDropdown } from 'data/endpoints';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import { FormFields, useForm } from 'features/shared/forms/hooks/useForm';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import { useQuery } from 'data/hooks/useQuery';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import { ContractStatus } from 'features/contracts/enums/contractStatus';
import ContractSelect from 'features/contracts/components/ContractSelect';
import IAdvanceFormService from 'features/requirements/services/IAdvanceFormService';
import { AdvanceFormSection_PageCard } from 'features/shared/components/loaders/promiseLoadingAreas';
import { trackPromise } from 'react-promise-tracker';
import AdvancesSubForm from 'features/requirements/components/advanceForm/AdvancesSubForm';
import AdvanceChangeParamsDTO from 'features/requirements/dtos/AdvanceChangeParamsDTO';
import AdvanceCanReduceDTO from 'features/requirements/dtos/AdvanceCanReduceDTO';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import AdvanceDisabledInput from 'features/requirements/components/advanceForm/AdvanceDisabledInput';
import { advanceFormValidations, setAdvancesValidations, validateAdvancesDecrease } from './advanceFormValidation';
import AdvanceReasonDialog from 'features/requirements/components/advanceForm/advanceReasonDialog/AdvanceReasonDialog';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import { useTranslation } from 'react-i18next';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';

const AdvanceFormSection = () => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const formService = useInjection<IAdvanceFormService>(IocTypes.AdvanceFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  //#region STORE
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;
  //#endregion STORE

  //#region STATE
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [advanceParams, setAdvanceParams] = useState<AdvanceChangeParamsDTO>({});
  const [advanceCanReduce, setAdvanceCanReduce] = useState<AdvanceCanReduceDTO>({});
  const [reasonDialogShow, setReasonDialogShow] = useState(false);
  //#endregion STATE

  //#region FETCH DATA
  let contractsQuery: DataQuery = {
    url: Customer_ContractDropdown,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: getQueryParams("ContractStatus", [ContractStatus.Effective])
  };
  const { data: contracts, loadStatus: contractsStatus } = useQuery<ContractDropdownDTO[]>(contractsQuery, [], [customer]);
  //#endregion FETCH DATA

  //#region FORM HOOK
  const { values, setFormValue, errors, bindField, bindUncontrolledField, valideteForm, validateFormField, setValidationRule } = useForm({
    initialValidations: advanceFormValidations()
  });
  //#endregion FORM HOOK

  //#region SIDE-EFFECTS
  useEffect(() => {
    // set default contract
    if (contracts.length === 1) {
      contractChangeHandler(contracts[0]);
    }
  }, [contracts])
  //#endregion SIDE-EFFECTS

  //#region HELPERS
  const fetchData = async (contractId: string) => {
    let params = await formService.fetchAdvanceParams(contractId!);
    let canReduce = await formService.fetchAdvanceCanReduce(contractId);

    return {
      params,
      canReduce
    };
  }

  const createRequirement = async (values: FormFields) => {
    let preparedValues = formService.prepareValuesToSend(values);
    let result = await createService.createRequirement("ZMENAZALOH", preparedValues);
    openRequirementResultDialog(false, { response: result });
  }
  //#endregion HELPERS

  //#region HANDLERS
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (valideteForm()) {
      if (validateAdvancesDecrease(advanceCanReduce, advanceParams.actualAdvanceSettings ?? [], values)) {
        createRequirement(values);
      } else {
        setReasonDialogShow(true);
      }
    }
  };

  const submitReasonDialogHandler = (reason: string) => {
    // hide dialog
    setReasonDialogShow(false); 

    // add reason to form values
    let valuesCopy = {...values};
    valuesCopy["DESC_TMzalohyDuvodSnizeni"] = reason;
    createRequirement(valuesCopy);
  }

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract && validateFormField("DESC_TMzalohySmlouva", contract.contractId)) {
      // clear dependent values
      formService.clearPreviousAdvanceValues(setFormValue);

      // fetch data
      const { params, canReduce } = await trackPromise(fetchData(contract.contractId!), AdvanceFormSection_PageCard);

      // set state
      setAdvanceParams(params);
      setAdvanceCanReduce(canReduce);
      setSelectedContract(contract);

      // set default values
      formService.setDefaulAdvanceValues(params, setFormValue);

      // set select data to form values
      setFormValue("DESC_TMzalohySmlouva", contract.contractId);

      // set validations
      setAdvancesValidations(params.actualAdvanceSettings ?? [], setValidationRule);
    }
  }
  //#endregion HANDLERS

  //#region RENDER
  return (
    <NoContentWrapper
      data={contracts}
      loadStatus={contractsStatus}
      noContentInfo={tLabels("advanceFormSection.contractNotFound")}
      noContentAsAlert
    >
      <form onSubmit={submitFormHandler}>
        <PageCard 
          title={tLabels("advanceFormSection.cardTitle")}
          bodyClassName="pt-4 px-4"
          loadingAreaName={AdvanceFormSection_PageCard}
        >
          <div className="row no-gutters mx-n2 position-relative">
            <div className="col-xl-6 px-2">
              <ContractSelect 
                name="DESC_TMzalohySmlouva"
                contracts={contracts}
                onChange={contractChangeHandler}
                errors={errors}
                defaultContract={selectedContract}
              />
            </div>
            <div className="col-lg-6 col-xl-3 px-2">
              <AdvanceDisabledInput 
                title={tLabels("advanceFormSection.changeFrom")}
                icon="calendar"
                value={advanceParams.minDate!}
                valueType="date"
                visible={baseFormService.isFilledValues(values, ["DESC_TMzalohySmlouva"])}
              />
            </div>
            <div className="col-lg-6 col-xl-3 px-2">
              <AdvanceDisabledInput 
                title={tLabels("advanceFormSection.advancesDistribution")}
                icon="chart-pie"
                value={advanceParams.advanceFrequency!}
                visible={baseFormService.isFilledValues(values, ["DESC_TMzalohySmlouva"])}
              />
            </div>
          </div>
        </PageCard>

        <AdvancesSubForm
          bindField={bindField}
          actualAdvances={advanceParams.actualAdvanceSettings!}
          editable={baseFormService.isFilledValues(values, ["DESC_TMzalohySmlouva"])}
          errors={errors}
        />

        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />

        <button type="submit" className="btn btn-primary px-3 py-2">{tLabels("advanceFormSection.sendRequirement")}</button>
      </form>

      <AdvanceReasonDialog 
        show={reasonDialogShow}
        setShow={setReasonDialogShow}
        onSubmit={submitReasonDialogHandler}
      />
    </NoContentWrapper>
  )
  //#endregion RENDER
}

export default AdvanceFormSection;
